import { useLayoutEffect, useState, useSyncExternalStore } from 'react';

// Holds one row of a list outside the list's item data, such as the selected
// row, so that a change re-renders only the row that loses it and the row
// that gets it. A list names the row by its key, which stays with the row
// when the list sorts again.
export interface RowStore<Row> {
    get: () => Row | null;
    set: (row: Row | null) => void;
    subscribe: (listener: () => void) => () => void;
}

export const createRowStore = <Row>(): RowStore<Row> => {
    let storedRow: Row | null = null;
    const listeners = new Set<() => void>();

    return {
        get: () => storedRow,
        set: (row) => {
            if (row === storedRow) {
                return;
            }
            storedRow = row;
            listeners.forEach((listener) => listener());
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
};

export const useStoreHoldsRow = <Row>(store: RowStore<Row>, row: Row) =>
    useSyncExternalStore(store.subscribe, () => store.get() === row);

/**
 * One store for the list's life that holds this row, such as the row of the
 * page that is open. It starts with the row, so rows that mount with the list
 * show it at once.
 */
export const useRowStoreHolding = <Row>(row: Row | null) => {
    const [store] = useState(() => {
        const created = createRowStore<Row>();
        created.set(row);
        return created;
    });
    useLayoutEffect(() => {
        store.set(row);
    }, [store, row]);
    return store;
};
